import { AppCategory } from './app.model';
import { Common } from './common.model';
import {
    Embedded,
    EmbeddedApp,
    EmbeddedCell,
    EmbeddedCompany,
    EmbeddedUser,
    toUserEmbedded,
    toCellEmbedded,
    toCompanyEmbedded,
    toCompaniesEmbedded,
    toAppsEmbedded,
    toTribeEmbedded,
    EmbeddedTribe,
    toUsersEmbedded,
} from './embed.model';
import { Link } from './link.model';
import { Sla, PeriodId, ServiceLevel } from './sla-period.model';
import { AssignmentTypes } from '@data/models/assignment-group-config';


export const LICENSE_METADATA_KEY = 'licensing/customer-license/spla';
export const LEGACY_PROJECT_TYPE_METADATA_KEY = 'legacy/project-type';
export const DOC_CONFLUENCE_METADATA_KEY = 'documentation/confluence/creation';

export type embed = keyof EmbedProperties;

export interface EmbedProperties {
    $createdBy: EmbeddedUser;
    $updatedBy: EmbeddedUser;
    $deletedBy: EmbeddedUser;
    $cell: { name: string; id: string };
    $customer: { id: string; name: string; corporateName: string };
    $partners: { id: string; name: string }[];
    $apps: { id: string; name: string; logo: string; category: AppCategory }[];
    $tribe: EmbeddedTribe;
    $buildProjectManager: EmbeddedUser;
    $deliveryManager: EmbeddedUser;
    $backupDeliveryManager: EmbeddedUser;
    $appManagers: EmbeddedUser[];
}

export type EmbedProject<T extends embed> = Project & Partial<Pick<EmbedProperties, T>>;

export const configuration: { [K in embed]: { embed: string; transform: (project: Project, embedded: Embedded) => EmbedProperties[K] } } = {
    $createdBy: {
        embed: 'users',
        transform: (data: Project, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'createdBy', embedded),
    },
    $updatedBy: {
        embed: 'users',
        transform: (data: Project, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'updatedBy', embedded),
    },
    $deletedBy: {
        embed: 'users',
        transform: (data: Project, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'deletedBy', embedded),
    },
    $cell: {
        embed: 'cells',
        transform: (data: Project, embedded: Embedded): EmbeddedCell => toCellEmbedded(data, 'cell', embedded),
    },
    $customer: {
        embed: 'companies',
        transform: (data: Project, embedded: Embedded): EmbeddedCompany => toCompanyEmbedded(data, 'customer', embedded),
    },
    $partners: {
        embed: 'companies',
        transform: (data: Project, embedded: Embedded): EmbeddedCompany[] => toCompaniesEmbedded(data, 'partners', embedded),
    },
    $apps: {
        embed: 'apps',
        transform: (data: Project, embedded: Embedded): EmbeddedApp[] => toAppsEmbedded(data, 'apps', embedded),
    },
    $tribe: {
        embed: 'tribes',
        transform: (data: Project, embedded: Embedded): EmbeddedTribe => toTribeEmbedded(data, 'tribe', embedded),
    },
    $buildProjectManager: {
        embed: 'users',
        transform: (data: Project, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'buildProjectManager', embedded),
    },
    $deliveryManager: {
        embed: 'users',
        transform: (data: Project, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'deliveryManager', embedded),
    },
    $backupDeliveryManager: {
        embed: 'users',
        transform: (data: Project, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'backupDeliveryManager', embedded),
    },
    $appManagers: {
        embed: 'users',
        transform: (data: Project, embedded: Embedded): EmbeddedUser[] => toUsersEmbedded(data, 'appManagers', embedded),
    },
};

export type ProjectWithRoles = Project & { roles: string[] };

export class Project extends Common {
    declare public id?: string;
    public name?: string;
    public language?: string;
    public status?: statusEnum;
    public customer?: string;
    public partners?: string[];
    public groups?: string[];
    public businessCode?: string;
    public businessLabel?: string;
    public buildProjectManager?: string;
    public deliveryManager?: string;
    public backupDeliveryManager?: string;
    public appManagerMailingList?: string;
    public appManagers?: string[];
    public apps?: string[];
    public cell?: string;
    public documentUri?: string;
    public avatarInitials?: string;
    public urgency?: ProjectUrgency;
    public periodReplyTime?: PeriodId;
    public periodResponseTime?: PeriodId;
    public sla?: Sla[];
    public defaultServiceLevel?: string;
    public serviceLevels?: ServiceLevel[];
    public links?: Link[];
    public tribe?: string;
    public licenses?: string[];

    constructor(data: any) {
        super(data);
        this.id = data.id;
        this.name = data.name;
        this.language = data.language;
        this.status = data.status;
        this.customer = data.customer;
        this.partners = data.partners;
        this.groups = data.groups ?? [];
        this.businessCode = data.businessCode ?? '';
        this.businessLabel = data.businessLabel ?? '';
        this.buildProjectManager = data.buildProjectManager;
        this.deliveryManager = data.deliveryManager;
        this.backupDeliveryManager = data.backupDeliveryManager;
        this.appManagerMailingList = data.appManagerMailingList;
        this.appManagers = data.appManagers;
        this.cell = data.cell;
        this.documentUri = data.documentUri;
        this.avatarInitials = data.avatarInitials;
        this.urgency = data.urgency;
        this.periodReplyTime = data.periodReplyTime;
        this.periodResponseTime = data.periodResponseTime;
        this.sla = data.sla;
        this.defaultServiceLevel = data.defaultServiceLevel;
        this.serviceLevels = data.serviceLevels;
        this.apps = data.apps;
        this.links = data.links ?? [];
        this.tribe = data.tribe;
        this.licenses = data.metadata?.[LICENSE_METADATA_KEY]?.split(',') ?? [];
    }

    public static toCustomProject<T extends embed>(data: Project, embedded: Embedded, customFields: (keyof EmbedProperties)[]): EmbedProject<T> {

        const customData = customFields.reduce((acc, field: embed) => {
            acc[field] = configuration[field].transform(data, embedded);
            return acc;
        }, {});

        if (customFields.includes('$customer')) {
            customData['$partner'] = data?.partners?.map(projectPartner => embedded.companies[projectPartner]);
        }
        // TODO
        // hacky
        // metadata has to be convert to map...
        return { ...new Project(data), ...customData } as EmbedProject<T>;
    }

    public static toProjectHttp(project: Project) {
        return { ...project, metadata: Object.fromEntries(project.metadata ?? []) };
    }

    public static toCustomTicketList<T extends embed>(data: Project[], embedded: Embedded, customFields: T[] = []): EmbedProject<T>[] {
        return data.map((ticket) => this.toCustomProject(ticket, embedded, customFields));
    }

}

export enum statusEnum {
    'BUILDING',
    'RELEASED',
    'CLOSED',
    'UNPROVISIONED',
    'UNPROVISIONING'
}

export enum ProjectUrgency {
    MUST = 'MUST',
    STANDARD = 'STANDARD',
    SMALL = 'SMALL'
}

export const PROJECT_URGENCY_OBJECT: { [key: string]: { value: ProjectUrgency; translate: string; tooltip?: string } } = {
    [ProjectUrgency.MUST]: {
        value: ProjectUrgency.MUST, translate: 'Critique'
    },
    [ProjectUrgency.SMALL]: {
        value: ProjectUrgency.SMALL, translate: 'Non-critique'
    },
    [ProjectUrgency.STANDARD]: {
        value: ProjectUrgency.STANDARD, translate: 'Standard'
    }
};

export interface ProjectRequest {
    reference: string;
    labelFr: string;
    labelEn: string;
    workedUnitCost: number;
    isServiceSupport: boolean;
    documentUri: string;
    guaranteedReplyTime: number;
    guaranteedResponseTime: number;
    guaranteedReplyTimeNbh: number;
    guaranteedResponseTimeNbh: number;
    periodResponseTime: string;
    template: string;
    isCustom: boolean;
    flatRateMinutes: number;
    assignmentType: AssignmentTypes;
}

export interface ProjectServiceLine {
    reference: string;
    labelFr: string;
    labelEn: string;
    isCustom: boolean;
    requests: ProjectRequest[];
}

export interface ProjectServiceCatalog {
    reference: string;
    name: string;
    isCustom: boolean;
    serviceLines: ProjectServiceLine[];
    flatRateEnabled: boolean;
}

export interface UnprovisionProjectInventoryRequest {
    title: string;
    description: string;
    dueDate: Date;
    projectId: string;
    inventoryIds: string[];
    additionalServiceLines: string[];
}

export interface Operation {
    path: string;
    op: 'ADD' | 'REMOVE' | 'REPLACE';
    value?: string | string[] | object;
}

export interface BulkEditProjectResponse {
    updatedProjectIds: string[];
    errors: { [key: string]: string };
}
